import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import CarCard from "@/components/CarCard";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const PAGE_SIZE = 12;

export default function VendutePage() {
  const { lang } = useLang();
  const [cars, setCars] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    api.get("/cars", { params: { page, limit: PAGE_SIZE, status: "sold", sort: "recent" } })
      .then((r) => {
        setCars(r.data || []);
        setTotal(Number(r.headers?.["x-total-count"] || 0));
      })
      .catch(() => { setCars([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const goTo = (p) => {
    const next = Math.min(totalPages, Math.max(1, p));
    if (next !== page) {
      setPage(next);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const buildPageList = () => {
    const pages = new Set([1, totalPages, page, page - 1, page + 1]);
    const arr = [...pages].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < arr.length; i++) {
      if (i > 0 && arr[i] - arr[i - 1] > 1) out.push("…");
      out.push(arr[i]);
    }
    return out;
  };

  return (
    <div className="pt-40 pb-32 px-6 sm:px-10 lg:px-14 max-w-[1600px] mx-auto" data-testid="vendute-page">
      <div className="flex items-center gap-4 mb-8">
        <span className="w-10 h-px bg-white/40" />
        <span className="label-eyebrow text-white/70">{lang === "en" ? "Archive" : "Archivio"}</span>
      </div>

      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        className="font-serif text-5xl sm:text-6xl lg:text-8xl font-light leading-[0.95] tracking-tighter text-white"
      >
        {lang === "en" ? "Cars" : "Vetture"}<br />
        <span className="italic text-neutral-500">{lang === "en" ? "sold." : "vendute."}</span>
      </motion.h1>

      <p className="mt-10 text-neutral-400 text-lg max-w-2xl font-light leading-relaxed">
        {lang === "en"
          ? "Some of the cars that have found a new owner. A testament to the work done and the quality of the selection."
          : "Alcune delle vetture che hanno trovato un nuovo proprietario. Una testimonianza del lavoro svolto e della qualità della selezione."}
      </p>

      {loading ? (
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-20">
          {[0, 1, 2].map((i) => (
            <div key={i} className="aspect-[4/3] bg-neutral-900/60 animate-pulse" />
          ))}
        </div>
      ) : cars.length === 0 ? (
        <div className="mt-24 border border-white/10 py-24 text-center">
          <p className="label-eyebrow text-neutral-500 mb-4">{lang === "en" ? "Archive being updated" : "Archivio in aggiornamento"}</p>
          <p className="font-serif italic text-2xl text-neutral-400">{lang === "en" ? "Sold cars will appear here." : "Le vetture vendute appariranno qui."}</p>
        </div>
      ) : (
        <>
          <div className="mt-10 mb-8 label-eyebrow text-neutral-500" data-testid="vendute-total-count">
            {total} {lang === "en" ? "cars sold" : "vetture vendute"} · {lang === "en" ? "page" : "pagina"} {page}/{totalPages}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-20">
            {cars.map((c, i) => (
              <CarCard key={c.id} car={c} index={i} hidePrice />
            ))}
          </div>

          <nav
            className="mt-20 flex items-center justify-center gap-2 flex-wrap"
            aria-label={lang === "en" ? "Pagination" : "Paginazione"}
            data-testid="vendute-pagination"
          >
            <button
              onClick={() => goTo(page - 1)}
              disabled={page <= 1}
              className="inline-flex items-center gap-2 border border-white/10 px-5 py-2.5 text-white hover:border-white/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="vendute-pagination-prev"
            >
              <ChevronLeft size={14} /> {lang === "en" ? "Previous" : "Precedente"}
            </button>
            {totalPages > 1 && buildPageList().map((p, idx) =>
              p === "…" ? (
                <span key={`e-${idx}`} className="px-3 text-neutral-500">…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => goTo(p)}
                  className={`min-w-[44px] px-3 py-2 border transition-colors ${
                    p === page
                      ? "bg-white text-black border-white"
                      : "text-white border-white/10 hover:border-white/40"
                  }`}
                  data-testid={`vendute-pagination-page-${p}`}
                >
                  {p}
                </button>
              )
            )}
            <button
              onClick={() => goTo(page + 1)}
              disabled={page >= totalPages}
              className="inline-flex items-center gap-2 border border-white/10 px-5 py-2.5 text-white hover:border-white/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="vendute-pagination-next"
            >
              {lang === "en" ? "Next" : "Successivo"} <ChevronRight size={14} />
            </button>
          </nav>
        </>
      )}
    </div>
  );
}
